import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { useMetrics } from "~/hooks";
import { Metrics } from "~/lib/arrow";
import { add, sub } from "~/lib/date";
import { useMemo } from "react";

interface UseSprintStreakCardParams {
  scope: SpaceOrWorkspaceScope;
}

export function useSprintStreakCard({ scope }: UseSprintStreakCardParams) {
  const { isFeatureEnabled } = useFeatureFlags();
  const isSprintStreakFeatureEnabled = isFeatureEnabled("Sprint Streak");
  const numOfDays = 10;

  const { timelineDates, startTime, endTime } = useMemo(
    // get +1 additional day so we can calculate the startRisk for the first day we display
    () => getDailyCountsTimeline(numOfDays + 1),
    [numOfDays],
  );

  const { historicalDailySpaceRiskMetricsData, isLoading } = useMetrics({
    entityMrn: scope.mrn,
    metricMrns: [Metrics.HistoricalDailySpaceRiskMetrics],
    startTime,
    endTime,
    skip: !isSprintStreakFeatureEnabled,
  });

  const spaceRiskForTimestamp = (timestamp: number) => {
    return (
      historicalDailySpaceRiskMetricsData.find(
        (d) => d["+timestamp"] === timestamp,
      )?.space_risk ?? 0.0
    );
  };

  const dailySpaceRisk = useMemo(
    () =>
      timelineDates
        .map((d) => {
          const spaceRisk = spaceRiskForTimestamp(d.getTime());
          const previousDay = sub(d, { days: 1 });
          const previousSpaceRisk = spaceRiskForTimestamp(
            previousDay.getTime(),
          );
          return {
            time: d.toISOString(),
            startRisk: previousSpaceRisk,
            endRisk: spaceRisk,
          };
        })
        .slice(-numOfDays),
    [stringify(historicalDailySpaceRiskMetricsData)],
  );

  const isEmpty = !isLoading && dailySpaceRisk.length === 0;

  return {
    isLoading,
    isEmpty,
    dailySpaceRisk,
  };
}

function getDailyCountsTimeline(numOfDays: number) {
  // Nexus returns a data bucket for each start of UTC day between date range.
  // We will end at the most recent bucket: the start of current utc day.
  const startOfUtcDay = new Date().setUTCHours(0, 0, 0, 0);

  // Sometimes nexus doesn't return a data bucket for some dates. To deal with
  // these holes in the timeline, we generate our own list of dates so that we
  // can identify any missing data buckets from the nexus response.
  const timelineDates = Array.from({ length: numOfDays }, (_, i) => {
    return sub(startOfUtcDay, { days: numOfDays - 1 - i });
  });

  const startDate = timelineDates[0];
  const endDate = timelineDates[numOfDays - 1];

  const startTime = startDate.toISOString();
  // The endTime input param is not inclusive, so we add a second to ensure
  // nexus includes our range's end date in the returned daily buckets.
  const endTime = add(endDate, { seconds: 1 }).toISOString();

  return { timelineDates, startTime, endTime };
}

export function signedFloat(val: number) {
  const clamped = clampFloat(Math.abs(val), 2);
  const signed = Math.sign(val);
  const sign = signed < 0 || Object.is(signed, -0) ? "–" : "+";
  return `${sign}${clamped}`;
}

export function clampFloat(
  float: number,
  precision: number,
  roundingMode?: Intl.NumberFormatOptions["roundingMode"],
) {
  const toFixed = new Intl.NumberFormat("en", {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
    roundingMode,
  }).format;
  return toFixed(float);
}

export function stringify(value: any): string {
  return JSON.stringify(value, (_, v) =>
    typeof v === "bigint" ? Number(v) : v,
  );
}
